import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TABS } from '@/constants';
import {
	getAvailableSlateMarketIds,
	getOpenUntradedMarketIds,
	getSlateAvailableMarkets,
} from '@/utils';
import {
	setSeenOpenMarketIds,
	setShouldAnimate,
	useSeenOpenMarketIdsSelector,
	useShouldAnimateSelector,
} from '@/store/slices';

interface UseLobbyOpenMarketsProps {
	markets: any[];
	userTrades: any[];
	slatesData: any[];
	lobbyId: string | undefined;
	activeTab: string;
	hasSeenInfo: boolean | undefined;
	slateTradeLimit: number | undefined;
	slateUserTradeCounts: Record<string, any> | undefined;
}

export function useLobbyOpenMarkets({
	markets,
	userTrades,
	slatesData,
	lobbyId,
	activeTab,
	hasSeenInfo,
	slateTradeLimit,
	slateUserTradeCounts,
}: UseLobbyOpenMarketsProps) {
	const dispatch: any = useDispatch();

	const openMarketIds = useMemo(
		() => getOpenUntradedMarketIds(markets, userTrades),
		[markets, userTrades]
	);

	const avlToTradeMarketCount = openMarketIds?.length;

	const prevCountRef = useRef(avlToTradeMarketCount);

	const isShouldAnimate = useShouldAnimateSelector(lobbyId);

	useEffect(() => {
		if (prevCountRef.current === 1 && avlToTradeMarketCount === 0) {
			dispatch((setShouldAnimate as any)({ lobbyId, value: true }));
		}

		if (avlToTradeMarketCount > 1 && isShouldAnimate) {
			dispatch((setShouldAnimate as any)({ lobbyId, value: false }));
		}

		prevCountRef.current = avlToTradeMarketCount;
	}, [dispatch, lobbyId, isShouldAnimate, avlToTradeMarketCount]);

	const seenOpenMarketIds = useSeenOpenMarketIdsSelector(lobbyId);

	useEffect(() => {
		if (activeTab === TABS.MARKETS && openMarketIds?.length > 0) {
			const newMarketIds = openMarketIds.filter((id: any) => !seenOpenMarketIds.includes(id));

			if (newMarketIds.length > 0) {
				dispatch(
					(setSeenOpenMarketIds as any)({
						lobbyId,
						marketIds: [...seenOpenMarketIds, ...newMarketIds],
					})
				);
			}
		}
	}, [activeTab, openMarketIds, seenOpenMarketIds, lobbyId, dispatch]);

	useEffect(() => {
		if (lobbyId && openMarketIds?.length && !hasSeenInfo) {
			dispatch((setSeenOpenMarketIds as any)({ lobbyId, marketIds: openMarketIds }));
		}
	}, [lobbyId, hasSeenInfo, dispatch, openMarketIds]);

	const slateAvailableMarkets = useMemo(
		() => getSlateAvailableMarkets(markets, slatesData, userTrades),
		[markets, slatesData, userTrades]
	);

	const availableMarkets = useMemo(
		() => getAvailableSlateMarketIds(slateAvailableMarkets, slateTradeLimit, slateUserTradeCounts),
		[slateAvailableMarkets, slateTradeLimit, slateUserTradeCounts]
	);

	const unseenOpenMarketCount = useMemo(() => {
		if (!openMarketIds?.length || !availableMarkets?.length) return 0;
		if (!seenOpenMarketIds?.length) return openMarketIds.length;
		return openMarketIds.filter(
			(id: any) => !seenOpenMarketIds.includes(id) && availableMarkets.includes(id)
		).length;
	}, [openMarketIds, seenOpenMarketIds, availableMarkets]);

	return {
		openMarketIds,
		avlToTradeMarketCount,
		isShouldAnimate,
		slateAvailableMarkets,
		availableMarkets,
		unseenOpenMarketCount,
	};
}
